import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { installAxiosInterceptors } from '../utils/axiosInterceptors';

const AuthContext = createContext(null);

const API = getApiUrl();

// iOS Fix: Configure axios globally to ALWAYS send credentials
axios.defaults.withCredentials = true;

// Phase-6 perf: in-flight dedup + short-TTL cache for reference-data GETs.
installAxiosInterceptors();

// iOS Fix: Detect if user is on iOS
const isIOS = () => {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// iOS Fix: Request Storage Access for cross-site cookie access (iOS 12.1+)
const requestStorageAccess = async () => {
  if (!isIOS()) return true;
  
  if (!document.requestStorageAccess) {
    console.warn('⚠️ Storage Access API not available on this browser');
    return false;
  }
  
  try {
    await document.requestStorageAccess();
    console.log('✅ iOS Storage Access granted');
    return true;
  } catch (err) {
    console.warn('⚠️ iOS Storage Access denied:', err.message);
    // Not critical - cookies may still work if same-origin
    return false;
  }
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []); // checkAuth is stable, no need to add to deps

  const checkAuth = async () => {
    try {
      // PRODUCTION FIX: Use token from localStorage if available (fallback for K8s)
      const token = localStorage.getItem('access_token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      // If auth fails and we have a token, clear it
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const loginAdmin = async (email, password) => {
    // iOS Fix: Request storage access before login
    if (isIOS()) {
      await requestStorageAccess();
    }
    
    const { data } = await axios.post(
      `${API}/auth/admin/login`,
      { email, password },
      { withCredentials: true }
    );
    
    // PRODUCTION FIX: Store tokens from response body as fallback
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
    }
    
    setUser(data);
    return data;
  };

  const loginStudent = async (first_name, last_name, school_name, grade) => {
    // iOS Fix: Request storage access before login
    if (isIOS()) {
      console.log('📱 iOS detected, requesting storage access...');
      await requestStorageAccess();
    }
    
    const { data } = await axios.post(
      `${API}/auth/student/login`,
      { first_name, last_name, school_name, grade },
      { withCredentials: true }
    );
    
    // PRODUCTION FIX: Store tokens from response body as fallback
    // In K8s environments, cookies may be unreliable due to load balancing/ingress
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      // Set Authorization header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      console.log('✅ Tokens stored in localStorage as fallback');
    }
    
    // iOS Fix: Log successful login for debugging
    if (isIOS()) {
      console.log('✅ iOS login successful, user data received:', {
        first_name: data.first_name,
        hasData: !!data.first_name,
        hasToken: !!data.access_token
      });
    }
    
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    // Clear tokens from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, loginAdmin, loginStudent, logout }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};